import React, { useCallback, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ValueMapping, FieldInfo } from '../../models';
import ValueMappingItem from './ValueMappingItem';
import { Button, Card } from '../ui';
import { RefreshCw } from '../ui/icons';

interface ValueMappingEditorProps {
  field: FieldInfo;
  valueMappings: ValueMapping[];
  onValueMappingsChange: (mappings: ValueMapping[]) => void;
}

const ValueMappingEditor: React.FC<ValueMappingEditorProps> = React.memo(({
  field,
  valueMappings = [],
  onValueMappingsChange,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize with default mappings if none exist
  useEffect(() => {
    if (valueMappings.length === 0 && field.defaultMappings) {
      const defaultMappings: ValueMapping[] = Object.entries(field.defaultMappings).map(([value, pattern], index) => ({
        pattern,
        value,
        order: index,
      }));
      onValueMappingsChange(defaultMappings);
    }
  }, [field.defaultMappings, valueMappings.length, onValueMappingsChange]);

  const handleResetToDefaults = useCallback(() => {
    if (field.defaultMappings) {
      const defaultMappings: ValueMapping[] = Object.entries(field.defaultMappings).map(([value, pattern], index) => ({
        pattern,
        value,
        order: index,
      }));
      onValueMappingsChange(defaultMappings);
    }
  }, [field.defaultMappings, onValueMappingsChange]);

  const handleRemoveMapping = useCallback((index: number) => {
    const updatedMappings = valueMappings.filter((_, i) => i !== index);
    // Reorder the remaining mappings
    const reorderedMappings = updatedMappings.map((mapping, i) => ({
      ...mapping,
      order: i,
    }));
    onValueMappingsChange(reorderedMappings);
  }, [valueMappings, onValueMappingsChange]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = valueMappings.findIndex(
        (mapping, index) => mapping.pattern + index === active.id
      );
      const newIndex = valueMappings.findIndex(
        (mapping, index) => mapping.pattern + index === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedMappings = arrayMove(valueMappings, oldIndex, newIndex);
        // Update order numbers
        const updatedMappings = reorderedMappings.map((mapping, i) => ({
          ...mapping,
          order: i,
        }));
        onValueMappingsChange(updatedMappings);
      }
    }
  }, [valueMappings, onValueMappingsChange]);

  const handlePatternChange = useCallback((index: number, pattern: string) => {
    const updatedMappings = valueMappings.map((mapping, i) => 
      i === index ? { ...mapping, pattern } : mapping
    );
    onValueMappingsChange(updatedMappings);
  }, [valueMappings, onValueMappingsChange]);

  const handleValueChange = useCallback((index: number, value: string) => {
    const updatedMappings = valueMappings.map((mapping, i) => 
      i === index ? { ...mapping, value } : mapping
    );
    onValueMappingsChange(updatedMappings);
  }, [valueMappings, onValueMappingsChange]);

  // Only show this editor for fields that have predefined options
  if (!field.values || Object.keys(field.values).length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <h3 className="text-base font-medium text-primary-600 mb-2">
        Value Mappings
      </h3>

      <p className="text-sm text-gray-600 mb-4">
        Configure how scraped text should be mapped to normalized values.
        Mappings are evaluated in order (top to bottom). Use ".*" as a fallback pattern.
        <strong> Drag and drop to reorder mappings.</strong>
      </p>

      {/* Existing mappings */}
      {valueMappings.length === 0 ? (
        <Card className="p-6 text-center bg-gray-50">
          <p className="text-sm text-gray-600">
            No value mappings configured. Click "Reset to Defaults" to use pre-configured mappings.
          </p>
        </Card>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              {valueMappings.length} mapping{valueMappings.length !== 1 ? 's' : ''} configured
            </p>

            {field.defaultMappings && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetToDefaults}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset to Defaults
              </Button>
            )}
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={valueMappings.map((mapping, index) => mapping.pattern + index)}
              strategy={verticalListSortingStrategy}
            >
              {valueMappings.map((mapping, index) => (
                <ValueMappingItem
                  key={mapping.pattern + index}
                  mapping={mapping}
                  index={index}
                  field={field}
                  onPatternChange={handlePatternChange}
                  onValueChange={handleValueChange}
                  onRemove={handleRemoveMapping}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
});

ValueMappingEditor.displayName = 'ValueMappingEditor';

export default ValueMappingEditor; 